import { updateWfhProfile } from '@/services/wfhProfile.service';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Drawer, Form, Button, InputNumber, TagPicker, Toggle } from 'rsuite';
import { useTranslation } from 'react-i18next';

interface EditDrawerViewProps {
  open: boolean;
  onClose: () => void;
  profile: any;
}
const dayOptions = [
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
  { label: 'Sunday', value: 'Sunday' }
];
const EditDrawerView: React.FC<EditDrawerViewProps> = ({ open, onClose, profile }) => {
  const dispatch = useDispatch();
  const [formValue, setFormValue] = React.useState<any>({
    name: '',
    authorizedWfhDays: [],
    daysPerWeekWithBalance: 0,
    daysPerWeekWithoutBalance: 0
  });

  React.useEffect(() => {
    if (profile) {
      setFormValue({
        name: profile.name || '',
        authorizedWfhDays: profile.authorizedWfhDays || [],
        daysPerWeekWithBalance: profile.daysPerWeekWithBalance || 0,
        daysPerWeekWithoutBalance: profile.daysPerWeekWithoutBalance || 0,
        autoAcceptIfAuthorizedDays: profile.autoAcceptIfAuthorizedDays || false,
        allowSubmitForUnauthorizedDays: profile.allowSubmitForUnauthorizedDays || false
      });
    }
  }, [profile]);

  const handleSubmit = () => {
    if (profile && profile._id) {
      dispatch(
        updateWfhProfile(profile._id, {
          name: formValue.name,
          authorizedWfhDays: formValue.authorizedWfhDays,
          daysPerWeekWithBalance: Number(formValue.daysPerWeekWithBalance),
          daysPerWeekWithoutBalance: Number(formValue.daysPerWeekWithoutBalance),
          autoAcceptIfAuthorizedDays: formValue.autoAcceptIfAuthorizedDays,
          allowSubmitForUnauthorizedDays: formValue.allowSubmitForUnauthorizedDays
        })
      );
      onClose();
    }
  };
  console.log('formValue', formValue);
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Edit Home Office Profile')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          {/* Name Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Name')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter profile name" />
          </Form.Group>

          {/* Authorized WFH Days Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Authorized Home Office Days')}</Form.ControlLabel>
            <TagPicker
              name="authorizedWfhDays"
              data={dayOptions}
              value={formValue.authorizedWfhDays}
              onChange={value => setFormValue(prev => ({ ...prev, authorizedWfhDays: value }))}
              placeholder={t('Select authorized home office days')}
              block
              searchable
            />
          </Form.Group>

          {/* Days Per Week With Balance Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Days Per Week With Balance')}</Form.ControlLabel>
            <InputNumber
              name="daysPerWeekWithBalance"
              min={0}
              value={formValue.daysPerWeekWithBalance}
              onChange={value => setFormValue(prev => ({ ...prev, daysPerWeekWithBalance: value }))}
              placeholder={t('Enter days per week with balance')}
              block
            />
          </Form.Group>

          {/* Days Per Week Without Balance Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Days Per Week Without Balance')}</Form.ControlLabel>
            <InputNumber
              name="daysPerWeekWithoutBalance"
              min={0}
              value={formValue.daysPerWeekWithoutBalance}
              onChange={value =>
                setFormValue(prev => ({ ...prev, daysPerWeekWithoutBalance: value }))
              }
              placeholder={t('Enter days per week without balance')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>
              {t('Auto-accept fully authorized Home Office requests')}{' '}
              <Form.HelpText tooltip>
                {t(
                  'When this option is enabled, a home office request is automatically accepted if all requested days match the authorized weekdays. If one or more days are not authorized, the request status will be set to pending and requires manual approval.'
                )}
              </Form.HelpText>
            </Form.ControlLabel>

            <Toggle
              onChange={(checked: boolean, event) =>
                setFormValue(prev => ({ ...prev, autoAcceptIfAuthorizedDays: checked }))
              }
              checked={formValue.autoAcceptIfAuthorizedDays}
            />
          </Form.Group>
          {(formValue.daysPerWeekWithoutBalance > formValue.authorizedWfhDays?.length ||
            formValue.daysPerWeekWithBalance > 0) && (
            <Form.Group>
              <Form.ControlLabel>
                {t('Allow Home Office requests on unauthorized days')}
                <Form.HelpText tooltip>
                  {t(
                    'If this option is enabled, users are allowed to submit home office requests even if some days are not part of the authorized WFH days defined in their profile. These requests will be either marked as pending or will consume balance if available, and as long as the weekly WFH limit is not exceeded. If this option is disabled, any submission that includes an unauthorized day will be automatically rejected, even if the weekly limit has not been reached'
                  )}
                </Form.HelpText>
              </Form.ControlLabel>
              <Toggle
                onChange={(checked: boolean, event) =>
                  setFormValue(prev => ({ ...prev, allowSubmitForUnauthorizedDays: checked }))
                }
                checked={formValue.allowSubmitForUnauthorizedDays}
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default EditDrawerView;
